
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated, useGetKnowledgeTagsBreakdown, useListOwnersAssignedUsersTasks, ListOwnerAssignments } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, CheckCircle, ChevronDown, ChevronRight, Clock, PenSquare, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import React, { useMemo, useState } from "react";
import { KnowledgeTaskCard } from "./components/KnowledgeTaskCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { TagsManagementView } from "./components/TagsManagementView";
import { KnowledgeCommonBlockCard } from "./components/KnowledgeCommonBlockCard";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "@lhc/permissions/stytch_original";
import { hasAccessToFeature } from "@/lib/models/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { KnowledgeSearchComponent } from "./components/KnowledgeSearchComponent";

import { AllTasksView } from "./allTasksView";
import { OwnersAssignedOutReport } from "./assignedOutReport";
import { KnowledgeAuditReports } from "./auditReports";
import { InProgressReport } from "./inProgressReport";

function generateFilterFromTab(tab: string) {
  switch (tab) {
    case "assigned":
      return "assigned";
    case "owned":
      return "owned";
    case "all":
      return "all";
    case "resources":
      return "common-blocks";
    default:
      return "all";
  }
}

export function KnowledgeTasksDashboard() {
  const { member } = useStytchMember();
  const navigate = useNavigate();
  const [tab, setTab] = useState("assigned");
  const [page, setPage] = useState(1);
  const [auditFilterDate, setAuditFilterDate] = useState(moment.tz(CommonDestinationTimezone).subtract(6, "months").startOf("day").toDate());
  const knowledgeItemsRes = useKnowledgeItemsPaginated({
    params: {
      page,
      filter: generateFilterFromTab(tab),
      last_audit_date: tab === "audit-review" ? { before: auditFilterDate } : undefined,
    },
    queryFn: fetchKnowledgeItemsPage,
    enabled: tab !== "tags",
  });

  const hasManagerPermission = hasAccessToFeature({
    name: "Knowledge Tasks",
    requiredPermissions: [PermissionType.Manager],
    requiredPackages: [PossiblePackage.KnowledgeTasks],
  }, member);

  const user_id = member?.trusted_metadata.db_user_id;
  const member_role = member?.trusted_metadata.role;
  const isAdmin = member_role === "admin";
  const canSeeOtherTasks = isAdmin || hasManagerPermission;


  const loadingOrErrorUi = GenerateLoadingOrError(tab !== 'tags' ? [knowledgeItemsRes] : []);

  if (loadingOrErrorUi && tab !== 'tags') {
    return (
      <section className="flex min-h-full flex-1 flex-col justify-start px-6 py-6 lg:px-8">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Tasks
        </h1>
        {loadingOrErrorUi}
      </section>
    );
  }

  const knowledgeItems = tab !== 'tags' ? (knowledgeItemsRes?.data?.data?.data || []) : [];

  const tasks = knowledgeItems.filter((item: KnowledgeApi.KnowledgeItem) => item.item_type === "task");
  const commonBlocks = knowledgeItems.filter((item: KnowledgeApi.KnowledgeItem) => item.item_type === "common-block");
  const assignedTasks = tasks.filter((item: KnowledgeApi.KnowledgeItem) => {
    return item?.assignments?.some((assignment: KnowledgeApi.KnowledgeAssignment) => assignment.assignee_id === user_id);
  });
  const ownedTasks = tasks.filter((item: KnowledgeApi.KnowledgeItem) => item.owner_id === user_id);
  const allTasks = tasks;

  const PaginationControls = ({ res }: { res: typeof knowledgeItemsRes }) => {
    const isLoading = res.isLoading;
    const info = res?.data?.data?.pagination || {};

    return (
      <div className="flex items-center justify-between mt-4">
        <Button
          onClick={() => setPage(page - 1)}
          disabled={page <= 1 || isLoading}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Previous</span>}
        </Button>

        <span>
          Page {info.currentPage || 1} of {info.totalPages || 1}
        </span>

        <Button
          onClick={() => setPage(page + 1)}
          disabled={page >= (info.totalPages || 1) || isLoading}
        >
          {isLoading ? <Loader className="w-4 h-4" /> : <span>Next</span>}
        </Button>
      </div>);
  }

  const onTabChange = (value: string) => {
    setTab(value);
    setPage(1);
  };

  return (
    <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Knowledge Center
        </h1>
        {tab !== 'tags' && (
          <Button
            onClick={() => navigate("/tasks/create")}
            className="flex items-center gap-2"
          >
            <PlusCircle className="h-4 w-4" />
            Create Task
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={onTabChange} className="space-y-6">
        <TabsList>
          <TabsTrigger value="assigned">Assigned To Me</TabsTrigger>
          <TabsTrigger value="owned">Owned Tasks</TabsTrigger>
          {canSeeOtherTasks && <TabsTrigger value="all">All Tasks</TabsTrigger>}
          {canSeeOtherTasks && <TabsTrigger value="tags">View Tags</TabsTrigger>}
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="audit-review">Audit Review</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="assigned_out">Assigned Out</TabsTrigger>
          {isAdmin && (<TabsTrigger value="in_progress_report">In Progress Report</TabsTrigger>)}
        </TabsList>

        <TabsContent value="assigned" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'assigned' ? <Loader /> : assignedTasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {assignedTasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`assigned-${task.id}`}
                    task={task}
                    isAssigned={true}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No assigned tasks</h3>
              <p className="text-sm text-muted-foreground">
                You don't have any tasks assigned to you yet.
              </p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="owned" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'owned' ? <Loader /> : ownedTasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {ownedTasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`owned-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No owned tasks</h3>
              <p className="text-sm text-muted-foreground">
                You haven't created any tasks yet.
              </p>
            </div>
          )}
        </TabsContent>
        {canSeeOtherTasks && (
          <TabsContent value="all" className="space-y-4">
            <AllTasksView currentPage={page} onPageChange={setPage} />
          </TabsContent>
        )}
        {canSeeOtherTasks && (
          <TabsContent value="tags" className="space-y-4">
            <TagsManagementView />
          </TabsContent>
        )}
        <TabsContent value="resources" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'resources' ? <Loader /> : commonBlocks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {commonBlocks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`common-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No Resources</h3>
              <p className="text-sm text-muted-foreground">
                You haven't created any resources yet.
              </p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="audit-review" className="space-y-4">
          {knowledgeItemsRes.isLoading && tab === 'audit-review' ? <Loader /> : tasks.length > 0 ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {tasks.map((task: KnowledgeApi.KnowledgeItem) => (
                  <KnowledgeTaskCard
                    key={`audit-review-${task.id}`}
                    task={task}
                    isAssigned={false}
                    assignmentCount={task.assignmentsCount || 0}
                  />
                ))}
              </div>
              <PaginationControls res={knowledgeItemsRes} />
            </>
          ) : (
            <div className="border rounded-lg p-6 bg-background text-center">
              <h3 className="font-medium mb-2">No Tasks Require Review</h3>
              <p className="text-sm text-muted-foreground">
                Checked all tasks requiring an review before {moment.tz(auditFilterDate, CommonDestinationTimezone).format("MM/DD/YYYY")}
              </p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="reports" className="space-y-4">
          <KnowledgeAuditReports />
        </TabsContent>
        <TabsContent value="assigned_out" className="space-y-4">
          <OwnersAssignedOutReport />
        </TabsContent>
        <TabsContent value="in_progress_report" className="space-y-4">
          <InProgressReport />
        </TabsContent>

      </Tabs>
    </section>
  );
}
